import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CarouselHero from '@/components/base/CarouselHero';
import { useCollection } from '@/hooks/useCollection';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { CHURCH_WORSHIP_PHOTOS } from '@/lib/churchPhotos';
import type { Ministry } from '@/lib/types';

const MINISTRY_ICONS = ['ri-user-heart-line', 'ri-heart-2-line', 'ri-building-line', 'ri-hand-heart-line'];

export default function ServicesPage() {
  const { rows: ministries, loading, error, reload } = useCollection<Ministry>('ministries', 'name', true);
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    document.title = 'Services — TGMCI Church Programs & Ministry';
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }); },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const serviceTimes = settings?.service_times?.length
    ? settings.service_times
    : [
        { label: 'Sunday', time: 'Morning Worship: 10:00 AM', location: '' },
        { label: 'Wednesday', time: 'Bible Study: 7:00 PM', location: '' },
        { label: 'Friday', time: 'Youth Program: 6:00 PM', location: '' },
      ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <CarouselHero
          title="Comprehensive Services for Community Empowerment"
          subtitle="Discover the programs and initiatives through which TGMCI transforms lives every day."
          images={CHURCH_WORSHIP_PHOTOS}
          breadcrumb="Services"
        />

        {/* Services List */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8 space-y-20">
            {loading ? (
              <p className="text-center text-foreground-400 py-10">
                <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading services...
              </p>
            ) : error ? (
              <div className="text-center py-10">
                <p className="text-red-600 text-sm mb-3">Could not load services.</p>
                <button type="button" onClick={reload} className="btn-outline">Retry</button>
              </div>
            ) : (
              ministries.map((service, idx) => (
                <article
                  key={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${idx % 2 !== 0 ? 'lg:grid-flow-col-dense' : ''}`}
                >
                  <div className={`reveal img-zoom rounded-2xl overflow-hidden h-[360px] ${idx % 2 !== 0 ? 'lg:col-start-2' : ''}`}>
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className={`${idx % 2 !== 0 ? 'reveal-right lg:col-start-1' : 'reveal-left'}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-600 text-white shrink-0">
                        <i className={`${MINISTRY_ICONS[idx % MINISTRY_ICONS.length]} text-xl`} aria-hidden="true" />
                      </div>
                      <span className="text-5xl font-heading font-bold text-background-200">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                      {service.name}
                    </h2>
                    <div className="w-10 h-1 bg-accent-500 mb-4" />
                    {service.leader && (
                      <p className="text-accent-600 font-label text-sm font-medium italic mb-4">Led by {service.leader}</p>
                    )}
                    <p className="text-foreground-600 text-base leading-relaxed mb-6">{service.description}</p>
                    {service.meeting_info && (
                      <p className="text-foreground-500 text-sm mb-6">
                        <i className="ri-time-line mr-1.5 text-accent-500" aria-hidden="true" />
                        {service.meeting_info}
                      </p>
                    )}
                    <Link to="/contact" className="btn-primary">
                      <i className="ri-user-add-line" aria-hidden="true" />
                      Get Involved
                    </Link>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

        {/* Service Times */}
        <section className="py-20 bg-background-100">
          <div className="max-w-[1000px] mx-auto px-4 md:px-8">
            <div className="text-center mb-12 reveal">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Plan Your Visit</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">Service Times</h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {serviceTimes.map((s, idx) => (
                <div
                  key={s.label}
                  className={`reveal p-7 rounded-xl text-center ${idx === 0 ? 'bg-primary-600 text-white' : 'bg-background-50 border border-background-200'}`}
                >
                  <div className={`w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-4 ${idx === 0 ? 'bg-white/20' : 'bg-primary-50'}`}>
                    <i className={`ri-time-line text-2xl ${idx === 0 ? 'text-white' : 'text-primary-600'}`} aria-hidden="true" />
                  </div>
                  <h3 className={`font-heading font-bold text-xl mb-4 ${idx === 0 ? 'text-white' : 'text-foreground-800'}`}>{s.label}</h3>
                  <p className={`text-sm mb-1 ${idx === 0 ? 'text-white/80' : 'text-foreground-500'}`}>{s.time}</p>
                  {s.location && <p className={`text-sm ${idx === 0 ? 'text-white/70' : 'text-foreground-400'}`}>{s.location}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary-700 text-center px-4">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Stay Connected with Our Community</h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">Subscribe to our newsletter for updates and events.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-accent">Get Involved</Link>
            <Link to="/give" className="btn-outline-white">Support TGMCI</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}